// ============================================================================
// TOASTCONTAINER.JS - CONTENEDOR GLOBAL DE NOTIFICACIONES TOAST
// ============================================================================

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useToastContext } from '../contexts/ToastContext';
import Toast from './Toast';
import '../styles/toast.css';

// Posiciones disponibles para el contenedor
const POSITIONS = {
  'top-left': 'toast-container-top-left',
  'top-center': 'toast-container-top-center', 
  'top-right': 'toast-container-top-right',
  'bottom-left': 'toast-container-bottom-left',
  'bottom-center': 'toast-container-bottom-center',
  'bottom-right': 'toast-container-bottom-right',
};

function ToastContainer({ 
  position = 'top-right',
  className = '',
  // Sin valor por defecto: el tope de alto lo pone toast.css con vh y dvh
  // (en móvil 100vh incluye la barra del navegador y se salía por abajo).
  maxHeight,
  gutter = 16 // Espacio entre toasts
}) {
  const { toasts, removeToast } = useToastContext();
  const containerRef = useRef(null);

  // ============================================================================
  // EFECTOS
  // ============================================================================

  // Crear el elemento del portal si no existe
  useEffect(() => {
    let portalElement = document.getElementById('toast-portal');
    
    if (!portalElement) {
      portalElement = document.createElement('div');
      portalElement.id = 'toast-portal';
      portalElement.setAttribute('aria-live', 'polite');
      portalElement.setAttribute('aria-atomic', 'true');
      document.body.appendChild(portalElement);
    }

    return () => {
      // Limpieza al desmontar (opcional, normalmente se mantiene)
      const portal = document.getElementById('toast-portal');
      if (portal && portal.children.length === 0) {
        document.body.removeChild(portal);
      }
    };
  }, []);

  // Manejar tecla Escape para cerrar toasts dismissibles
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        // Cerrar el toast más reciente que sea dismissible
        const dismissibleToast = toasts
          .slice()
          .reverse()
          .find(toast => toast.dismissible);
          
        if (dismissibleToast) {
          removeToast(dismissibleToast.id);
        }
      }
    };

    if (toasts.length > 0) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [toasts, removeToast]);

  // ============================================================================
  // MANEJADORES
  // ============================================================================

  const handleToastRemove = (toastId) => {
    removeToast(toastId);
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // Si no hay toasts, no renderizar nada
  if (toasts.length === 0) {
    return null;
  }

  // Obtener la clase CSS de posición
  const positionClass = POSITIONS[position] || POSITIONS['top-right'];

  const containerContent = (
    <div 
      ref={containerRef}
      className={`toast-container ${positionClass} ${className}`}
      style={{ 
        '--toast-gutter': `${gutter}px`,
        ...(maxHeight ? { maxHeight } : {})
      }}
      role="region"
      aria-label="Notificaciones"
    >
      {toasts.map((toast, index) => {
        // Calcular z-index para que los más nuevos estén encima
        const zIndex = 1000 + (toasts.length - index);
        
        return (
          <div
            key={toast.id}
            className="toast-wrapper"
            style={{ 
              zIndex,
              // Animación de entrada escalonada
              animationDelay: `${index * 100}ms`
            }}
          >
            <Toast 
              toast={toast}
              onRemove={handleToastRemove}
              className={`toast-index-${index}`}
            />
          </div>
        );
      })}
      
      {/* Indicador de más toasts (si hay muchos) */}
      {toasts.length > 5 && (
        <div className="toast-overflow-indicator">
          <span className="toast-count">+{toasts.length - 5} más</span>
        </div>
      )}
    </div>
  );

  // Renderizar usando portal para evitar problemas de z-index
  const portalElement = document.getElementById('toast-portal');
  
  if (!portalElement) {
    console.warn('Toast portal element not found. Toasts may not display correctly.');
    return null;
  }

  return createPortal(containerContent, portalElement);
}

// ============================================================================
// COMPONENTE DE CONFIGURACIÓN AVANZADA (OPCIONAL)
// ============================================================================

// HOC para configurar el contenedor globalmente
export function withToastConfig(Component, config = {}) {
  return function ToastConfiguredComponent(props) {
    return (
      <>
        <Component {...props} />
        <ToastContainer {...config} />
      </>
    );
  };
}

// ============================================================================
// HOOKS AUXILIARES
// ============================================================================

// Hook para controlar el contenedor desde componentes
export function useToastContainer() {
  const { toasts, clearAllToasts, getToastCount } = useToastContext();
  
  return {
    toastCount: toasts.length,
    hasToasts: toasts.length > 0,
    clearAll: clearAllToasts,
    getCount: getToastCount
  };
}

export default ToastContainer;